import React, { useState, useEffect, useContext } from 'react';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    // get as _get,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';
import { NoRowsOverlay,LoadingOverlay } from '../../../components/xgrid/overlays';

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import ConfirmationDialog from '../../../components/confirmationdlg';

import UpgradeService from '../../../data/upgrade-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers from '../../../libraries/helpers';

import { purple, normal } from '../../../data/colors';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    progressLabel: {
        position: 'relative',
        zIndex: '10',
        top: '38px',
        width: '100%',
        marginTop: '-50px',
        textAlign: 'center',
    },
    progressContainer: {
        width: '100%',
    },
    progressHeight: {
        height: '30px',
        borderRadius: '3px',
        marginTop: '30px',
    },
    progressBar: {
        height: '25px',
        borderRadius: '3px',
    },
    colorPrimary: {
        background: purple[400],
        height: '30px',
        borderRadius: '3px',
    },
    statusMessage: {
        position: 'relative',
        top: '55px',
        zIndex: 10,
        marginTop: '-50px',
        textAlign: 'center'
    },
    colorPrimaryError: {
        background: theme.palette.error.main,
        height: '30px',
        borderRadius: '3px',
    },
    colorPrimaryDone: {
        background: normal,
        height: '30px',
        borderRadius: '3px',
    },
    colorPrimaryUploading: {
        background: purple[200],
        height: '30px',
        borderRadius: '3px',
    },
    tableHeaderActions: {
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
        borderBottom: 'none',
    },
    popoverContainer: {
        background: theme.palette.common.black,
        border: `2px solid ${theme.palette.background.paper}`,
        bordeRadius: '3px',
    },
}));

const DataNodeUpgrade = () => {
    const classes = useStyles();
    const theme = useTheme();
    const [preStage, setPreStage] = useState([]);
    const [upgrades, setUpgrades] = useState([]);
    const [staged, setStaged] = useState([]);
    const [deployed, setDeployed] = useState([]);
    const [uploadFile, setUploadFile] = useState({ name: '', content: null });
    const [selectedFile, setSelectedFile] = useState(null);
    const [openDeleteStaged, setOpenDeleteStaged] = useState(false);
    const [openDeleteDeployed, setOpenDeleteDeployed] = useState(false);
    const [openDeploy, setOpenDeploy] = useState(false);
    const [deleteMenu, setDeleteMenu] = useState(null);
    const { height } = useWindowSize();
    const tableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [columnWidth, setColumnWidth] = useState({});
    const datanodeUpgradePerms = Auth.permissions.get('DatanodeUpgrades');

    useEffect(() => {
        getUpgradeFiles();
        setColumnWidth(getProfile('datanode_upgrade').widths || {});
    }, []);

    useEffect(() => {
        if (uploadFile && uploadFile.name) {
            setPreStage({
                id: Helpers.UniqueID(),
                name: uploadFile.name,
                modified: new Date().toISOString(),
                status: 'Uploading',
                progress: 0,
            });
            handleUploadFile();
        }
    }, [uploadFile]);

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('datanode_upgrade', {
            ...getProfile('datanode_upgrade'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const getUpgradeFiles = () => {
        let u = [];

        UpgradeService.listUpgradeFiles('datanode', 'default').then(res => {
            if (res.stagedFiles) {
                const s = res.stagedFiles.map(v => ({ ...v, progress: 100, status: 'Complete', id: Helpers.UniqueID() }));
                setStaged(s);
                u = _cloneDeep(s);
            } else {
                setStaged([]);
            }
            if (res.deployedFiles) {
                const d = res.deployedFiles.map(v => ({ ...v, id: Helpers.UniqueID() }));
                setDeployed(d);
                d.forEach((v) => {
                    if (u.filter(x => x.name === v.name).length) {
                        return;
                    }
                    u.push(v);
                });
            } else {
                setDeployed([]);
            }
            setUpgrades(u);
        }, err => {
            toast.error(err);
        });
    };

    const handleGetFile = (e) => {
        setUploadFile(e.target.files[0]);
    };

    const handleUploadFile = () => {
        UpgradeService.uploadUpgradeFile('datanode', uploadFile).then(res => {
            toast.success('Install file uploaded successfully');
            getUpgradeFiles();
            setPreStage([]);
            setSelectedFile(null);
        }, err => {
            toast.error(`${err.message.replace('erorr', 'error')}:  ${err.error}`);
            setPreStage([]);
        });
    };

    const tableRenderers = {
        fileName: (d) => (<div>{d.row.name}</div>),
        updated: (d) => (<div>{Moment(d.row.modified).format(theme.date.long)}</div>),
        progress: (d) => {
            return (
                <Box className={classes.progressContainer}>
                    <Box className={classes.statusMessage}>{d.row.status || 'Complete'}</Box>
                    <LinearProgress
                        variant={d.row.status !== 'Uploading' ? 'determinate' : 'indeterminate'}
                        value={100}
                        classes={{
                            barColorPrimary: d.row.status !== 'Uploading' ? (d.row.status === 'Failed' ? classes.colorPrimaryError : classes.colorPrimaryDone) : classes.colorPrimaryUploading,
                            determinate: classes.progressHeight,
                            indeterminate: classes.progressHeight,
                        }}
                    />
                </Box>
            );
        },
        deployed: (d) => {
            const i = deployed.findIndex(v => v.name === d.row.name);
            return (
                <Box>
                    {i !== -1 ? Moment(deployed[i].modified).format(theme.date.long) : 'Not Deployed'}
                </Box>
            );
        },
    };

    const handleDeleteStagedFile = () => {
        handleToggleDeleteStaged();
        UpgradeService.deleteUpgradeFile('datanode', 'Staged', selectedFile.name, 'default').then(res => {
            toast.success('Staged file deleted successfully', {
                onOpen: () => {
                    getUpgradeFiles();
                    setPreStage([]);
                    setSelectedFile(null);
                }
            });
        });
    };

    const handleDeleteDeployedFile = () => {
        handleToggleDeleteDeployed();
        UpgradeService.deleteUpgradeFile('datanode', 'Deployed', selectedFile.name, 'default').then(res => {
            toast.success('Deployed file deleted successfully', {
                onOpen: () => {
                    getUpgradeFiles();
                    setSelectedFile(null);
                },
            });
        });
    };

    const handleDeployStagedFile = () => {
        handleToggleDeploy();
        UpgradeService.deployUpgradeFile('datanode', selectedFile.name, 'default').then(res => {
            toast.success('Datanode will be deployed when a CMC upgrade is deployed');
            getUpgradeFiles();
        }, err => {
            toast.error(err);
        });
    };

    const handleSetSelected = (s) => {
        setSelectedFile(s.row);
    };

    const handleToggleDeleteStaged = () => {
        setOpenDeleteStaged(!openDeleteStaged);
    };

    const handleToggleDeleteDeployed = () => {
        setOpenDeleteDeployed(!openDeleteDeployed);
    };

    const handleToggleDeploy = () => {
        setOpenDeploy(!openDeploy);
    };

    const handleToggleDeleteMenu = (e) => {
        setDeleteMenu(e.currentTarget);
    };

    const handleCloseDeleteMenu = () => {
        setDeleteMenu(null);
    };

    return (
        <Grid container spacing={3}>
            <Grid container item xs={12} justifyContent="flex-end">
                {datanodeUpgradePerms.write && <Box>
                    <input
                        id="datanode-upgrade-file"
                        style={{ display: 'none' }}
                        type="file"
                        onClick={(e) => { e.target.value = null }}
                        onChange={handleGetFile}
                    />
                    <label htmlFor="datanode-upgrade-file">
                        <Button
                            variant="contained"
                            color="primary"
                            component="span"
                            aria-label="upload"
                        ><AddIcon /> Upload</Button>
                    </label>
                </Box>}
            </Grid>
            <Grid container item xs={12}>
                <Box width="100%" height={height - 310}>
                    {(datanodeUpgradePerms.delete || datanodeUpgradePerms.admin) && <Box className={classes.tableHeaderActions} p={1} display="flex" justifyContent="flex-end">
                        {datanodeUpgradePerms.delete && <Box display="flex">
                            <Box ml={2}>
                                <Tooltip title="Delete">
                                    <span>
                                        <IconButton
                                            disabled={!selectedFile}
                                            color="secondary"
                                            aria-label="delete"
                                            onClick={handleToggleDeleteMenu}
                                            size="large">
                                            <DeleteIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Menu open={Boolean(deleteMenu)} anchorEl={deleteMenu} onClose={handleCloseDeleteMenu}>
                                    <Box display={selectedFile ? (staged.findIndex(v => v.name === selectedFile.name) !== -1) ? 'block' : 'none' : 'none'}>
                                        <MenuItem onClick={handleToggleDeleteStaged}>Delete Staged</MenuItem>
                                    </Box>
                                    <Box display={selectedFile ? (deployed.findIndex(v => v.name === selectedFile.name) !== -1) ? 'block' : 'none' : 'none'}>
                                        <MenuItem onClick={handleToggleDeleteDeployed}>Delete Deployed</MenuItem>
                                    </Box>
                                </Menu>
                            </Box>
                        </Box>}
                        {datanodeUpgradePerms.admin && <Box display="flex">
                            <Box ml={2}>
                                <Tooltip title="Deploy">
                                    <Box mt={1}>
                                        <Button
                                            disabled={!selectedFile}
                                            color="primary"
                                            aria-label="deploy"
                                            onClick={handleToggleDeploy}
                                        >
                                            Deploy
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>}
                    </Box>}
                    <DataGridPro
                        apiRef={tableAPIRef}
                        rows={Array.prototype.concat(preStage, upgrades)}
                        components={{ LoadingOverlay:LoadingOverlay,
                                      NoRowsOverlay:NoRowsOverlay}}
                        columns={[
                            { sortable: true, width: columnWidth['File Name'] || 0, ...(!columnWidth['File Name'] ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'File Name', field: 'file_name', renderCell: tableRenderers.fileName },
                            { sortable: true, width: columnWidth.Uploaded || 0, ...(!columnWidth.Uploaded ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Uploaded', field: 'create', renderCell: tableRenderers.updated },
                            { sortable: true, width: columnWidth['Upload Progress'] || 0, ...(!columnWidth['Upload Progress'] ? { flex: 0.7 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Upload Progress', field: 'progress', renderCell: tableRenderers.progress },
                            { sortable: true, width: columnWidth['Deployed At'] || 0, ...(!columnWidth['Deployed At'] ? { flex: 0.7 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Deployed At', field: 'deployed', renderCell: tableRenderers.deployed },
                        ]}
                        disableMultipleColumnsSorting
                        disableMultipleSelection
                        onRowClick={handleSetSelected}
                        onColumnResize={handleColumnResize}
                    />
                </Box>
            </Grid>
            <ConfirmationDialog
                onClose={handleToggleDeleteStaged}
                onOK={handleDeleteStagedFile}
                open={openDeleteStaged}
                message={`Are you sure you want to delete the staged file ${selectedFile && selectedFile.name}?`}
                title="Delete staged file"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleDeleteDeployed}
                onOK={handleDeleteDeployedFile}
                open={openDeleteDeployed}
                message={`Are you sure you want to delete the deployed file ${selectedFile && selectedFile.name}?`}
                title="Delete deployed file"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleDeploy}
                onOK={handleDeployStagedFile}
                open={openDeploy}
                message={`Are you sure you want to deploy the file ${selectedFile && selectedFile.name} for upgrade? Data node upgrades are deployed when CMC upgrades are deployed.`}
                title="Deploy staged file for upgrade"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </Grid>
    );
};

export default DataNodeUpgrade;
